import React, { ReactElement, useState, useEffect } from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import {
  ReferAFriend as UIFoundationsRAF,
  TextHorizontalAlignments,
  InputTextField as UIFoundationsInputTextField,
  InputTextFieldStates,
  ReferItemProps,
  useThemeUtils,
  ContainerBorders,
  ContainerBorderRadiuses,
  ContainerBorderSizes,
  Container,
  ContainerPaddings
} from '@dcxwam/dcx-wam-ui-foundations'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../../functional/ErrorBoundary'
import {
  REPLACE_MESSAGE_TO_SHARE,
  REPLACE_CODE
} from '../../../../utils/constants'
import { useDashboardData } from '../common/use-dashboard-data'
import MgmReferAFriendEditConfig, {
  ReferAFriendModelProps
} from './ReferAFriendEditConfig'
import { replacePlaceholders } from '../../../../utils/replace-placeholders'
import {
  getChannelEntries,
  handleCopyOption,
  SuccessCopyMessageComponent
} from '../common/utils'
import { EditableComponentPropsType } from '../../../../utils/prop-types'
import withComponentRefresh from '../../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../../utils/aem/editor/extractEditableProps'
import { pushDataLayerReferAFriendSend } from '../common/data-layer'

interface AdditionalContentForCopyInputFieldProps {
  value?: string
  'aria-label'?: string
}

const AdditionalContentForCopyInputField: React.FC<
  AdditionalContentForCopyInputFieldProps
> = ({
  value,
  'aria-label': ariaLabel
}: {
  value?: string
  'aria-label'?: string
}): ReactElement => {
  const themeUtils = useThemeUtils()
  const configInputField = {
    defaultPadding: false,
    value,
    textAlign: TextHorizontalAlignments.Center,
    state: InputTextFieldStates.ReadOnly,
    inputProps: {
      'aria-label': ariaLabel
    }
  }
  const containerConfig = {
    border: ContainerBorders.Alternative,
    borderRadius: ContainerBorderRadiuses.Size_1,
    borderSize: ContainerBorderSizes.Small,
    padding: themeUtils.getResponsiveProp([{ x: ContainerPaddings.Size_4 }])
  }
  return (
    <Container {...containerConfig}>
      <UIFoundationsInputTextField {...configInputField} />
    </Container>
  )
}

const ReferAFriend: React.FC<
  EditableComponentPropsType<ReferAFriendModelProps>
> = withComponentRefresh((props) => {
  const {
    id,
    apiUrl,
    messageToShare,
    nativeShareDescriptiveText,
    shareWithAFriendCtaLabel,
    communicationChannels,
    copy,
    ...rest
  } = props.model
  const editableProps = useExtractEditableProps(props, rest)
  const [navigatorCanShare, setNavigatorCanShare] = useState(false)
  const dashboardData = useDashboardData()
  const themeUtils = useThemeUtils()

  const layoutWidths = themeUtils.getBreakpointsValues(
    themeUtils.getResponsiveProp([null, null, null, 0, null])
  )

  const lgBreakpoint = Object.keys(layoutWidths)?.find(
    (key: any) => layoutWidths[key] === 0 // eslint-disable-line @typescript-eslint/no-explicit-any
  )

  const message = replacePlaceholders(messageToShare, [
    [REPLACE_CODE, dashboardData?.mgmReferralCode]
  ])

  const nativeShareText = replacePlaceholders(nativeShareDescriptiveText, [
    [REPLACE_CODE, dashboardData?.mgmReferralCode]
  ])

  const shareData = {
    text: message
  }

  const onClickCopy = () => {
    navigator.clipboard.writeText(dashboardData?.mgmReferralCode || '')
    pushDataLayerReferAFriendSend(copy?.ctaLabel)
    return <SuccessCopyMessageComponent content={copy?.successMessage} />
  }

  useEffect(() => {
    const handleResize = () => {
      if ('share' in navigator) {
        const canNavigatorShare = navigator.canShare(shareData)
        if (window.innerWidth < Number(lgBreakpoint) && canNavigatorShare) {
          setNavigatorCanShare(true)
        } else {
          setNavigatorCanShare(false)
        }
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const encodeMessageToShare =
    messageToShare && message && encodeURIComponent(message)

  const channelEntries: Array<ReferItemProps> = getChannelEntries(
    communicationChannels || [],
    REPLACE_MESSAGE_TO_SHARE,
    encodeMessageToShare
  )

  if (copy && Object.keys(copy).length > 0) {
    let copyIndex: number = channelEntries?.length

    if (copy.position && copy.position - 1 <= channelEntries?.length) {
      copyIndex = copy.position - 1
    }
    channelEntries?.splice(
      copyIndex,
      0,
      handleCopyOption(
        copy,
        <AdditionalContentForCopyInputField
          value={dashboardData?.mgmReferralCode}
          aria-label={shareWithAFriendCtaLabel}
        />,
        onClickCopy
      )
    )
  }
  const handleNavigatorShare = async () => {
    if (navigator?.share) {
      try {
        await navigator.share(shareData)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err)
      }
    }
  }

  const configReferAFriend = {
    entries: channelEntries,
    nativeShareOnClick: handleNavigatorShare,
    nativeShare: navigatorCanShare,
    shareWithFriendLabel: shareWithAFriendCtaLabel,
    descriptiveTextNativeShare: nativeShareText
  }

  return (
    <EditableComponent {...editableProps}>
      {MgmReferAFriendEditConfig.isEmpty(props.model)
        ? MgmReferAFriendEditConfig.emptyLabel
        : channelEntries?.length > 0 && (
            // eslint-disable-next-line react/jsx-indent
            <UIFoundationsRAF {...configReferAFriend} />
          )}
    </EditableComponent>
  )
})

const ReferAFriendWithErrorBoundary = withErrorBoundary(ReferAFriend, {
  componentName: 'ReferAFriend',
  fallback: <ErrorFallback componentName="ReferAFriend" />
})

export default ReferAFriendWithErrorBoundary
